import { Link } from "react-router-dom";
import useAuth from "common/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { HistoryEvent } from "types/HistoryEvent";
import { IoPaw } from "react-icons/io5";
import { FiEye, FiBox } from "react-icons/fi";
import { BsFeather } from "react-icons/bs";
import { FaPlaneDeparture } from "react-icons/fa";
import { GiCheckboxTree, GiFamilyTree } from "react-icons/gi";
import { TableCellsIcon } from "@heroicons/react/24/solid";
import { TbFence } from "react-icons/tb";
import { PiTagLight, PiTreePalmLight } from "react-icons/pi";
import clsx from "clsx";
import dayjs from "dayjs";
import { BellAlertIcon } from "@heroicons/react/24/outline";

function HistoryItemLogo(key: string) {
  switch (key) {
    case "animal":
      return <IoPaw className="w-5 h-5 mr-2" />;
    case "enclosure-update":
    case "enrichment-request":
    case "enrichment-response":
    case "enclosure":
      return <TbFence className="w-5 h-5 mr-2" />;
    case "genus":
      return <GiFamilyTree className="w-5 h-5 mr-2" />;
    case "observation":
      return <FiEye className="w-5 h-5 mr-2" />;
    case "observation-grid":
      return <TableCellsIcon className="w-5 h-5 mr-2" />;
    case "observation-group":
      return <FiEye className="w-5 h-5 mr-2" />;
    case "observation-response":
      return <FiEye className="w-5 h-5 mr-2" />;
    case "sector":
      return <PiTreePalmLight className="w-5 h-5 mr-2" />;
    case "species":
      return <BsFeather className="w-5 h-5 mr-2" />;
    case "superfamily":
      return <GiCheckboxTree className="w-5 h-5 mr-2" />;
    case "tag":
      return <PiTagLight className="w-5 h-5 mr-2" />;
    case "event":
      return <BellAlertIcon className="w-5 h-5 mr-2" />;
    case "unavailability":
      return <FaPlaneDeparture className="w-5 h-5 mr-2" />;
    case "training-exercise":
    case "training-request":
    case "training":
      return <FiBox className="w-5 h-5 mr-2" />;
    default:
      return null;
  }
}

const getColorFromHistoryItem = (action: string) => {
  switch (action) {
    case "create":
    case "validate":
    case "import":
      return "border-green-500 border-4 border-double";
    case "delete":
    case "deny":
      return "border-red-500 border-4 border-double";
    case "update":
    case "ask":
    case "rate":
      return "border-blue-500 border-4 border-double";
    case "export":
      return "border-slate-300 border-4 border-double";
    default:
      return "border-slate-150 border-4 border-double";
  }
};

const getLinkFromHistoryItem = (modelName: string, entityId: number) => {
  switch (modelName) {
    case "animal":
      return `/animals/edit/${entityId}`;
    case "enclosure":
      return `/enclosures/edit/${entityId}`;
    // case "genus":
    //   return `/genera/edit/${entityId}`;
    case "observation":
      return null; // `/observations/edit/${entityId}`;
    case "observation-grid":
      return `/observation-grids/edit/${entityId}`;
    case "observation-group":
      return `/observation-groups/edit/${entityId}`;
    case "observation-response":
      return `/observation-response/${entityId}`;
    case "sector":
      return `/sectors/edit/${entityId}`;
    case "species":
      return `/species/edit/${entityId}`;
    case "superfamily":
      return `/superfamilies/edit/${entityId}`;
    case "tag":
      return `/tags/edit/${entityId}`;
    case "training-exercise":
      return `/training-exercises/edit/${entityId}`;
    case "training-request":
    case "training":
    case "enrichment-request":
    case "enrichment-response":
    case "enclosure-update":
    case "event":
    case "unavailability":
    default:
      return null;
  }
};

function HistoryItem({ item }: any) {
  const { user: currentUser } = useAuth();
  const canEditItem = currentUser?.isSuperadmin || currentUser?.isAdmin;

  const { requestType, modelName, entityName, entityId, author, createdAt } =
    item as HistoryEvent;

  const editLink = getLinkFromHistoryItem(modelName, entityId);

  const { t } = useTranslation();

  const containerClassName = clsx(
    "flex px-4 py-4 mt-2 w-full relative bg-white cursor-pointer rounded-lg border border-slate-150 transition-all",
    getColorFromHistoryItem(requestType.toLowerCase())
  );

  return (
    <div className={containerClassName}>
      <Link
        to={editLink ?? ""}
        className={
          !canEditItem && editLink
            ? "pointer-events-none flex flex-1"
            : "flex flex-1"
        }
      >
        <div className="flex flex-col flex-1 justify-between pl-4 pr-6 mb-2">
          <div className="flex mt-4 mb-2 gap-3">
            <div className="flex-1 flex flex-row justify-between items-center">
              <div className="flex items-center text-sm">
                {HistoryItemLogo(modelName)}
                <span className="font-bold">{`${author.username} ${t(
                  `history.requestTypeAction.${requestType.toLowerCase()}`
                )} ${t(`history.modelNameInSentence.${modelName}`, {
                  entityName,
                })}`}</span>
              </div>
              <div className="flex items-center text-sm">
                <span className="font-bold">
                  {dayjs(createdAt).format("LLLL")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default HistoryItem;
